import React, { useEffect, useMemo, useState } from 'react';

import { UploadOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  Divider,
  Form,
  GetProp,
  Image,
  Input,
  Modal,
  Row,
  Select,
  Upload,
  UploadFile,
  UploadProps,
  message,
} from 'antd';
import PhoneInput from 'antd-phone-input';
import TextArea from 'antd/es/input/TextArea';
import { UploadChangeParam } from 'antd/es/upload';
import { RcFile } from 'antd/es/upload/interface';
import { AxiosProgressEvent } from 'axios';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import appI18n from '../../../../config/i18n';
import { ApiErrorData } from '../../../../models/ApiResponse';
import { Country } from '../../../../models/Country';
import { ItemInterface } from '../../../../models/ItemInterface';
import { User } from '../../../../models/User';
import { VerticalService } from '../../../../services/VerticalService';
import { FunctionsHelper } from '../../../../utils/FunctionsHelper';

interface NewServiceModalProps {
  isOpen: boolean;
  handleModalCancel: () => void;
  submit: (
    countryCode: string,
    name: string,
    title: string,
    description: string,
    logoUrl: string,
    mainContactName: string,
    mainContactDialCode: string,
    mainContactPhone: string,
    mainContactEmail: string,
    primaryColor: string
  ) => void;
  countries: Country[];
}

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

const getBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

function NewServiceModal({
  isOpen,
  handleModalCancel,
  submit,
  countries,
}: NewServiceModalProps) {
  const authUser: User = useSelector((state: any) => state.auth);
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation(undefined, { i18n: appI18n });

  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');

  useEffect(() => {
    form.resetFields();
    setFileList([]);
    form.setFieldValue('phone', {
      ...form.getFieldValue('phone'),
      countryCode: parseInt(
        FunctionsHelper.getDialCode(authUser.company.countryCode)
      ),
    });
  }, [isOpen]);

  const onFinish = async (values: any) => {
    if (fileList.length === 0) {
      messageApi.error(
        t(
          'scenes.settings.services.components.new_service_modal.errors.logo_upload_required'
        )
      );
      return;
    }

    const file = fileList[0];
    const logoUrl = file.response?.url;
    const statusFile = file.status;

    switch (statusFile) {
      case 'uploading':
        messageApi.info(
          t(
            'scenes.settings.services.components.new_service_modal.messages.logo_uploading'
          )
        );
        return;
      case 'error':
        messageApi.error(
          t(
            'scenes.settings.services.components.new_service_modal.errors.logo_upload_failed'
          )
        );
        return;
    }

    if (!logoUrl) {
      messageApi.error(
        t(
          'scenes.settings.services.components.new_service_modal.errors.logo_upload_failed'
        )
      );
      return;
    }

    setLoading(true);

    await submit(
      values.country,
      values.name,
      values.title,
      values.description,
      logoUrl,
      values.fullName,
      `+${values.phone.countryCode}`,
      `${values.phone.areaCode}${values.phone.phoneNumber}`,
      values.email,
      values.primaryColor
    );

    setLoading(false);
  };

  const countryOptions = useMemo<ItemInterface[]>(() => {
    return countries.map((record) => ({
      value: record.code,
      label: record.name,
    }));
  }, [countries]);

  const handleChangeCountry = (newValue?: string) => {
    let dialCode = FunctionsHelper.getDialCode(authUser.company.countryCode);

    if (newValue) {
      const country = countries.find((record) => {
        return record.code === newValue;
      });

      if (country) {
        dialCode = FunctionsHelper.getDialCode(country.code);
      }
    }

    if (!form.getFieldValue('phone').phoneNumber) {
      form.setFieldValue('phone', {
        ...form.getFieldValue('phone'),
        countryCode: parseInt(dialCode),
      });
    }
  };

  const beforeUpload = (file: FileType) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
    if (!isJpgOrPng) {
      messageApi.error(
        t(
          'scenes.settings.services.components.new_service_modal.errors.logo_only_jpg_png'
        )
      );
      return Upload.LIST_IGNORE;
    }
    const isLt10MB = file.size / 1024 / 1024 <= 4;
    if (!isLt10MB) {
      messageApi.error(
        t(
          'scenes.settings.services.components.new_service_modal.errors.logo_max_size'
        )
      );
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  const handleChange = ({
    fileList: newFileList,
  }: UploadChangeParam<UploadFile>) => {
    setFileList(newFileList);
  };

  const handlePreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj as FileType);
    }

    setPreviewImage(file.url || (file.preview as string));
    setPreviewOpen(true);
  };

  const uploadProps: UploadProps = {
    beforeUpload: beforeUpload,
    fileList,
    listType: 'picture-card',
    showUploadList: true,
    onChange: handleChange,
    maxCount: 1,
    onPreview: handlePreview,
  };

  return (
    <>
      {contextHolder}

      <Modal
        title={t('scenes.settings.services.components.new_service_modal.title')}
        open={isOpen}
        onCancel={handleModalCancel}
        maskClosable={false}
        destroyOnClose
        footer={null}
        width={480}
      >
        <Divider style={{ marginTop: '15px', marginBottom: '15px' }} />

        <Form form={form} layout="vertical" onFinish={onFinish}>
          <fieldset>
            <legend>
              {t(
                'scenes.settings.services.components.new_service_modal.sections.service_info'
              )}
            </legend>

            <Row gutter={24}>
              <Col xs={24} lg={24}>
                <Form.Item
                  name="country"
                  label={t(
                    'scenes.settings.services.components.new_service_modal.labels.country'
                  )}
                  rules={[
                    {
                      required: true,
                      message: t('common.validations.required'),
                    },
                  ]}
                >
                  <Select
                    style={{ width: '100%' }}
                    options={countryOptions}
                    disabled={loading}
                    placeholder={t(
                      'scenes.settings.services.components.new_service_modal.placeholders.select_option'
                    )}
                    onChange={(value) => handleChangeCountry(value)}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col xs={24} lg={24}>
                <Form.Item
                  name="name"
                  label={t(
                    'scenes.settings.services.components.new_service_modal.labels.name'
                  )}
                  rules={[
                    {
                      required: true,
                      message: t(
                        'scenes.settings.services.components.new_service_modal.validations.name.required'
                      ),
                    },
                  ]}
                >
                  <Input
                    type="text"
                    disabled={loading}
                    placeholder={t(
                      'scenes.settings.services.components.new_service_modal.placeholders.name'
                    )}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col xs={24} lg={24}>
                <Form.Item
                  name="title"
                  label={t(
                    'scenes.settings.services.components.new_service_modal.labels.title'
                  )}
                  rules={[
                    {
                      required: true,
                      message: t(
                        'scenes.settings.services.components.new_service_modal.validations.title.required'
                      ),
                    },
                  ]}
                >
                  <Input
                    type="text"
                    disabled={loading}
                    placeholder={t(
                      'scenes.settings.services.components.new_service_modal.placeholders.title'
                    )}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col xs={24} lg={24}>
                <Form.Item
                  name="primaryColor"
                  label={t(
                    'scenes.settings.services.components.new_service_modal.labels.primary_color'
                  )}
                  rules={[
                    {
                      required: true,
                      message: t(
                        'scenes.settings.services.components.new_service_modal.validations.primary_color.required'
                      ),
                    },
                  ]}
                >
                  <Input
                    placeholder={t(
                      'scenes.settings.services.components.new_service_modal.placeholders.primary_color'
                    )}
                    type="color"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col xs={24} lg={24}>
                <Form.Item
                  name="description"
                  label={t(
                    'scenes.settings.services.components.new_service_modal.labels.description'
                  )}
                  rules={[
                    {
                      required: true,
                      message: t(
                        'scenes.settings.services.components.new_service_modal.validations.description.required'
                      ),
                    },
                  ]}
                >
                  <TextArea
                    placeholder={t(
                      'scenes.settings.services.components.new_service_modal.placeholders.description'
                    )}
                    rows={3}
                    disabled={loading}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col xs={24} lg={24}>
                <Form.Item
                  label={t(
                    'scenes.settings.services.components.new_service_modal.labels.logo'
                  )}
                  required={true}
                >
                  <Upload
                    {...uploadProps}
                    disabled={loading}
                    customRequest={async ({
                      file,
                      onSuccess,
                      onError,
                      onProgress,
                    }: any) => {
                      const formData = new FormData();
                      formData.append('file', file as RcFile);

                      try {
                        const response = await VerticalService.uploadLogoFile(
                          formData,
                          (progressEvent: AxiosProgressEvent) => {
                            if (onProgress && progressEvent.total) {
                              const percent = Math.round(
                                (progressEvent.loaded * 100) /
                                  progressEvent.total
                              );
                              onProgress({ percent });
                            }
                          }
                        );

                        if (response.success) {
                          onSuccess?.(response.data);
                        } else {
                          const error = response.data as ApiErrorData;
                          throw new Error(
                            error.message ||
                              t(
                                'scenes.settings.services.components.new_service_modal.errors.logo_upload_server_failed'
                              )
                          );
                        }
                      } catch (error: any) {
                        onError?.(error);
                      }
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <button
                      style={{
                        border: 0,
                        background: 'none',
                        cursor: 'pointer',
                      }}
                      type="button"
                      disabled={loading}
                    >
                      <UploadOutlined />
                      <div style={{ marginTop: 8 }}>
                        {t(
                          'scenes.settings.services.components.new_service_modal.buttons.upload_image'
                        )}
                      </div>
                    </button>
                  </Upload>
                </Form.Item>
                {previewImage && (
                  <Image
                    wrapperStyle={{ display: 'none' }}
                    preview={{
                      visible: previewOpen,
                      onVisibleChange: (visible) => setPreviewOpen(visible),
                      afterOpenChange: (visible) =>
                        !visible && setPreviewImage(''),
                    }}
                    src={previewImage}
                    onDrop={() => {
                      form.setFieldValue('avatar', undefined);
                      console.log('util');
                    }}
                  />
                )}
              </Col>
            </Row>
          </fieldset>

          <fieldset>
            <legend>
              {t(
                'scenes.settings.services.components.new_service_modal.sections.contact_info'
              )}
            </legend>

            <Col xs={24} lg={24}>
              <Form.Item
                name="fullName"
                label={t(
                  'scenes.settings.services.components.new_service_modal.labels.full_name'
                )}
                rules={[
                  {
                    required: true,
                    message: t(
                      'scenes.settings.services.components.new_service_modal.validations.full_name.required'
                    ),
                  },
                ]}
              >
                <Input
                  type="text"
                  disabled={loading}
                  placeholder={t(
                    'scenes.settings.services.components.new_service_modal.placeholders.full_name'
                  )}
                />
              </Form.Item>
            </Col>

            <Row gutter={24}>
              <Col xs={24} lg={24}>
                <Form.Item
                  name="email"
                  label={t(
                    'scenes.settings.services.components.new_service_modal.labels.email'
                  )}
                  rules={[
                    {
                      required: true,
                      message: t(
                        'scenes.settings.services.components.new_service_modal.validations.email.required'
                      ),
                    },
                    {
                      type: 'email',
                      message: t(
                        'scenes.settings.services.components.new_service_modal.validations.email.invalid'
                      ),
                    },
                  ]}
                >
                  <Input
                    type="text"
                    disabled={loading}
                    placeholder={t(
                      'scenes.settings.services.components.new_service_modal.placeholders.email'
                    )}
                    addonAfter={'@'}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col xs={24}>
                <Form.Item
                  name="phone"
                  label={t(
                    'scenes.settings.services.components.new_service_modal.labels.phone'
                  )}
                  rules={[
                    {
                      validator: (_, value) => {
                        if (value.valid()) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error(
                            t(
                              'scenes.settings.services.components.new_service_modal.validations.phone.invalid'
                            )
                          )
                        );
                      },
                    },
                  ]}
                >
                  <PhoneInput
                    enableSearch
                    searchNotFound={t(
                      'scenes.settings.services.components.new_service_modal.phone.search_not_found'
                    )}
                    searchPlaceholder={t(
                      'scenes.settings.services.components.new_service_modal.phone.search_placeholder'
                    )}
                    placeholder={t(
                      'scenes.settings.services.components.new_service_modal.placeholders.phone'
                    )}
                    disabled={loading}
                  />
                </Form.Item>
              </Col>
            </Row>
          </fieldset>

          <Row gutter={24}>
            <Col xs={24}>
              <Button
                onClick={() => form.submit()}
                loading={loading}
                type="primary"
                block
              >
                {t(
                  'scenes.settings.services.components.new_service_modal.buttons.save_changes'
                )}
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
}

export default NewServiceModal;
